import { useStore } from "@nanostores/react";
import { ReactElement, useState } from "react";
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { toast } from "react-toastify";

import {
  parseCSVHeaders,
  transformToApiMapping,
  uploadCsvAndGetJobId,
} from "../services/csv-upload";
import { $router } from "../stores/router";
import { CSVUploadFormData } from "../types/form-inputs";
import { Button } from "./catalyst/Button";
import { Dialog } from "./catalyst/Dialog";
import { DialogActions } from "./catalyst/DialogActions";
import { DialogBody } from "./catalyst/DialogBody";
import { DialogTitle } from "./catalyst/DialogTitle";
import { FileUpload } from "./csv-import/FileUpload";
import { StepIndicator } from "./csv-import/StepIndicator";
import { CSVColumnMapper } from "./CSVColumnMapper";
import { CSVRelationshipConfigurator } from "./CSVRelationshipConfigurator";
import { MultiStepNavigation, MultiStepWizard, Step } from "./MultiStepWizard";

interface CSVUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CSVUploadModal = ({
  isOpen,
  onClose,
}: CSVUploadModalProps): ReactElement => {
  const router = useStore($router);
  const [activeStep, setActiveStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<null | string>(null);

  const graphId = router?.route === "graph" ? router.params.id : null;

  const methods = useForm<CSVUploadFormData>({
    defaultValues: {
      columnsMappings: [],
      csvFile: "",
      relationships: [],
    },
  });

  const { clearErrors, handleSubmit, setError, setValue } = methods;

  const { append: appendColumnMapping, remove: removeAllColumnMappings } =
    useFieldArray({
      control: methods.control,
      name: "columnsMappings",
    });

  const handleClose = (): void => {
    onClose();
    if (!isUploading) {
      setActiveStep(0);
      setCsvFile(null);
      setUploadError(null);
      methods.reset();
    }
  };

  const totalSteps = 4;

  const stepTitles = [
    "Upload File",
    "Map Columns",
    "Configure Relationships",
    "Review & Import",
  ];

  const onSubmit: SubmitHandler<CSVUploadFormData> = async (data) => {
    if (activeStep === totalSteps - 1) {
      if (!graphId) {
        toast.error("Graph ID not found");
        return;
      }
      if (!csvFile) {
        toast.error("CSV file not found");
        return;
      }

      setIsUploading(true);
      setUploadError(null);
      try {
        const mapping = transformToApiMapping(data);
        const jobId = await uploadCsvAndGetJobId(graphId, csvFile, mapping);

        if (jobId) {
          toast.info("CSV upload started. Processing in background.");
        } else {
          toast.success("CSV data imported successfully!");
        }
        handleClose();
      } catch (error) {
        console.error("CSV upload error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to upload CSV";
        setUploadError(errorMessage);
      } finally {
        setIsUploading(false);
      }
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleFileSelect = async (file: File): Promise<void> => {
    clearErrors("csvFile");

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("csvFile", {
        message: "Please select a valid CSV file",
        type: "manual",
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("csvFile", {
        message: "File size must be less than 10MB",
        type: "manual",
      });
      return;
    }

    try {
      const fileContent = await file.text();
      const headers = parseCSVHeaders(fileContent);
      if (headers.length === 0) {
        setError("csvFile", {
          message: "CSV file appears to be empty or invalid",
          type: "manual",
        });
        return;
      }
      setCsvFile(file);
      setValue("csvFile", file.name);
      removeAllColumnMappings();
      headers.forEach((headerName) => {
        if (headerName.trim()) {
          appendColumnMapping({
            entityId: "",
            ignore: "true",
            name: headerName.trim(),
          });
        }
      });
    } catch (error) {
      console.error("Error reading CSV file:", error);
      setError("csvFile", {
        message: "Failed to read CSV file. Please check the file format.",
        type: "manual",
      });
    }
  };

  const stepContent = (
    <MultiStepWizard
      activeStep={activeStep}
      buttonsDisabled={isUploading}
      progressIndicator={StepIndicator}
      setActiveStep={setActiveStep}
      showButtons={false}
    >
      <Step nextLabel="Next" onNext={() => {}} title="Upload File">
        <FileUpload
          control={methods.control}
          errors={methods.formState.errors}
          maxRows={null}
          onFileSelect={handleFileSelect}
        />
      </Step>
      <Step nextLabel="Next" onNext={() => {}} title="Map Columns">
        <CSVColumnMapper />
      </Step>
      <Step nextLabel="Next" onNext={() => {}} title="Configure Relationships">
        <CSVRelationshipConfigurator />
      </Step>
      <Step nextLabel="Import" onNext={() => {}} title="Review & Import">
        <div className="space-y-4">
          {isUploading ? (
            <div className="rounded-lg border border-zinc-600 bg-zinc-800 p-4">
              <div className="mb-2 text-sm text-gray-300">
                Uploading and processing CSV data...
              </div>
            </div>
          ) : uploadError ? (
            <div className="rounded-lg border border-red-700 bg-red-950/20 p-4">
              <div className="mb-2 text-sm font-medium text-red-200">
                Upload failed
              </div>
              <p className="text-sm text-red-200">{uploadError}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-zinc-600 bg-zinc-800 p-4">
              <p className="text-sm text-gray-300">
                Ready to import data into your graph...
              </p>
            </div>
          )}
        </div>
      </Step>
    </MultiStepWizard>
  );

  return (
    <Dialog onClose={handleClose} open={isOpen} size="xl">
      <DialogTitle>{stepTitles[activeStep]}</DialogTitle>
      <DialogBody>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>{stepContent}</form>
        </FormProvider>
      </DialogBody>
      <DialogActions>
        <Button onClick={handleClose} plain>
          Close
        </Button>
        <FormProvider {...methods}>
          <MultiStepNavigation
            activeStep={activeStep}
            buttonsDisabled={isUploading}
            onNext={handleSubmit(onSubmit)}
            setActiveStep={setActiveStep}
            totalSteps={totalSteps}
          >
            {stepContent.props.children}
          </MultiStepNavigation>
        </FormProvider>
      </DialogActions>
    </Dialog>
  );
};
